import { SimpleEventDispatcher } from "@utils/helpers/simple-event-dispatcher"

export const scrollLockClass = "highlighter--scrolllock"

const EVENT_NAME: keyof HTMLElementEventMap = "touchmove"

/**
 * ScrollLock helps us to lock the scroll when using a touch device
 * and dragging. It's used both in both `ContextMenuSelectionModule.svelte`
 * as well as in `selection-logic.ts`
 * 
 * It can "stack" locked resources in case there's some crossover when
 * there are multiple highlighter listeners (like in search and highlights),
 * but I haven't given it much thought and I don't think it's necessary.
 */
export class ScrollLock extends SimpleEventDispatcher<{"onscroll": undefined}> {
    private lockedSources: Set<string | symbol>
    private element: HTMLElement
    private boundScrollHandler: (event: Event) => void
    private boundScrollEmitter: (event: Event) => void

    constructor(element: HTMLElement) {
        super()
        this.element = element
        this.lockedSources = new Set()

        this.boundScrollHandler = this.scrollHandler.bind(this)
        this.boundScrollEmitter = this.scrollEmit.bind(this)
        this.element.addEventListener(EVENT_NAME, this.boundScrollEmitter, { passive: false })
    }

    private scrollEmit(): void {
        if (!this.isLocked()) this.dispatch("onscroll")
    }

    private scrollHandler(event: Event): void {
        if (this.isLocked()) {
            event.preventDefault()
        }
    }

    /**
     * Lock the scroll container. Check references in your IDE for usage
     */
    public lock(source: string | symbol): void {
        if (!this.isLocked()) {
            this.element.addEventListener(EVENT_NAME, this.boundScrollHandler, { passive: false })
        }
        this.lockedSources.add(source)
    }

    public unlock(source: string | symbol): void {
        this.lockedSources.delete(source)
        if (!this.isLocked()) {
            this.element.removeEventListener(EVENT_NAME, this.boundScrollHandler)
        }
    }

    public isLocked(): boolean {
        return this.lockedSources.size > 0
    }

    public destroy(): void {
        this.element.removeEventListener(EVENT_NAME, this.boundScrollHandler)
        this.element.removeEventListener(EVENT_NAME, this.boundScrollEmitter)
    }
}
